/**
 * SKU Management Modal
 * Manages SKU mappings for a specific product across marketplaces
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  Edit2,
  Save,
  Package,
  Loader2,
  AlertCircle,
  Check
} from 'lucide-react';
import { useLanguage } from '../../i18n';
import { useModalA11y } from '../../hooks/useModalA11y';
import {
  apiGetProductSKUs,
  apiSaveSKU,
  apiUpdateSKU,
  apiDeleteSKU,
  apiGetAllMarketplaces,
  SKUData
} from '../../services/api';

interface Product {
  id: string;
  name: string;
  category: string;
  base_cost?: number;
}

interface Marketplace {
  id: string;
  name: string;
  short_name: string;
  icon: string;
  countries: Array<{
    code: string;
    name: string;
    currency: string;
    currencySymbol: string;
    flag: string;
  }>;
}

interface SKUManagementModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

const SKUManagementModal: React.FC<SKUManagementModalProps> = ({
  product,
  isOpen,
  onClose,
  onUpdate
}) => {
  const { language } = useLanguage();
  const modalRef = useModalA11y(isOpen, onClose);
  const [skus, setSkus] = useState<SKUData[]>([]);
  const [marketplaces, setMarketplaces] = useState<Marketplace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add/Edit form state
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    marketplace_id: '',
    country_code: '',
    sku: '',
    asin: '',
    listing_price: '',
    fulfillment_type: 'FBA',
    status: 'active'
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, product.id]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [skuData, mpData] = await Promise.all([
        apiGetProductSKUs(product.id),
        apiGetAllMarketplaces()
      ]);
      setSkus(skuData);
      setMarketplaces(mpData as Marketplace[]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      marketplace_id: marketplaces[0]?.id || '',
      country_code: marketplaces[0]?.countries[0]?.code || '',
      sku: '',
      asin: '',
      listing_price: '',
      fulfillment_type: 'FBA',
      status: 'active'
    });
    setIsAdding(true);
    setEditingId(null);
  };

  const handleEdit = (sku: SKUData) => {
    setFormData({
      marketplace_id: sku.marketplace_id,
      country_code: sku.country_code,
      sku: sku.sku,
      asin: sku.asin || '',
      listing_price: sku.listing_price?.toString() || '',
      fulfillment_type: sku.fulfillment_type || 'FBA',
      status: sku.status || 'active'
    });
    setEditingId(sku.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({
      marketplace_id: '',
      country_code: '',
      sku: '',
      asin: '',
      listing_price: '',
      fulfillment_type: 'FBA',
      status: 'active'
    });
  };

  const handleSave = async () => {
    if (!formData.marketplace_id || !formData.country_code || !formData.sku) {
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        product_id: product.id,
        marketplace_id: formData.marketplace_id,
        country_code: formData.country_code,
        sku: formData.sku,
        asin: formData.asin || undefined,
        listing_price: formData.listing_price ? parseFloat(formData.listing_price) : undefined,
        fulfillment_type: formData.fulfillment_type,
        status: formData.status
      };

      if (editingId) {
        await apiUpdateSKU(editingId, data);
      } else {
        await apiSaveSKU(data);
      }

      await loadData();
      handleCancel();
      onUpdate?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(language === 'tr' ? 'Bu SKU\'yu silmek istediğinize emin misiniz?' : 'Are you sure you want to delete this SKU?')) {
      return;
    }

    try {
      await apiDeleteSKU(id);
      await loadData();
      onUpdate?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const getSelectedMarketplace = () => {
    return marketplaces.find(m => m.id === formData.marketplace_id);
  };

  const getCountries = () => {
    const mp = getSelectedMarketplace();
    return mp?.countries || [];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sku-mgmt-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 id="sku-mgmt-modal-title" className="text-lg font-semibold text-slate-800">
              {language === 'tr' ? 'SKU Yönetimi' : 'SKU Management'}
            </h2>
            <p className="text-sm text-slate-500">{product.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
            aria-label={language === 'tr' ? 'Kapat' : 'Close'}
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" aria-hidden="true" />
              <span className="sr-only">{language === 'tr' ? 'Yükleniyor' : 'Loading'}</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          ) : (
            <>
              {/* Add/Edit Form */}
              {(isAdding || editingId) && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="text-sm font-medium text-slate-700 mb-4">
                    {editingId
                      ? (language === 'tr' ? 'SKU Düzenle' : 'Edit SKU')
                      : (language === 'tr' ? 'Yeni SKU Ekle' : 'Add New SKU')
                    }
                  </h3>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Marketplace */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Marketplace
                      </label>
                      <select
                        value={formData.marketplace_id}
                        onChange={(e) => {
                          const mp = marketplaces.find(m => m.id === e.target.value);
                          setFormData({
                            ...formData,
                            marketplace_id: e.target.value,
                            country_code: mp?.countries[0]?.code || ''
                          });
                        }}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {marketplaces.map(mp => (
                          <option key={mp.id} value={mp.id}>
                            {mp.icon} {mp.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Country */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {language === 'tr' ? 'Ülke' : 'Country'}
                      </label>
                      <select
                        value={formData.country_code}
                        onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {getCountries().map(c => (
                          <option key={c.code} value={c.code}>
                            {c.flag} {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* SKU */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        SKU *
                      </label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        placeholder="SKU-001"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* ASIN */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        ASIN
                      </label>
                      <input
                        type="text"
                        value={formData.asin}
                        onChange={(e) => setFormData({ ...formData, asin: e.target.value })}
                        placeholder="B00XXXXXX"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Listing Price */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {language === 'tr' ? 'Satış Fiyatı' : 'Listing Price'}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.listing_price}
                        onChange={(e) => setFormData({ ...formData, listing_price: e.target.value })}
                        placeholder="29.99"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Fulfillment */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Fulfillment
                      </label>
                      <select
                        value={formData.fulfillment_type}
                        onChange={(e) => setFormData({ ...formData, fulfillment_type: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="FBA">FBA</option>
                        <option value="FBM">FBM</option>
                        <option value="FBM-US">FBM-US</option>
                        <option value="FBM-TR">FBM-TR</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        {language === 'tr' ? 'Durum' : 'Status'}
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="active">{language === 'tr' ? 'Aktif' : 'Active'}</option>
                        <option value="inactive">{language === 'tr' ? 'Pasif' : 'Inactive'}</option>
                        <option value="out_of_stock">{language === 'tr' ? 'Stok Yok' : 'Out of Stock'}</option>
                      </select>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleSave}
                      disabled={isSaving || !formData.sku}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {language === 'tr' ? 'Kaydet' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 rounded-lg"
                    >
                      {language === 'tr' ? 'İptal' : 'Cancel'}
                    </button>
                  </div>
                </div>
              )}

              {/* SKU List */}
              {skus.length === 0 && !isAdding ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">
                    {language === 'tr'
                      ? 'Bu ürün için henüz SKU eklenmemiş'
                      : 'No SKUs added for this product yet'}
                  </p>
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4" />
                    {language === 'tr' ? 'İlk SKU\'yu Ekle' : 'Add First SKU'}
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left text-xs font-medium text-slate-500 uppercase py-3 px-2">
                          Marketplace
                        </th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase py-3 px-2">
                          {language === 'tr' ? 'Ülke' : 'Country'}
                        </th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase py-3 px-2">
                          SKU
                        </th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase py-3 px-2">
                          ASIN
                        </th>
                        <th className="text-right text-xs font-medium text-slate-500 uppercase py-3 px-2">
                          {language === 'tr' ? 'Fiyat' : 'Price'}
                        </th>
                        <th className="text-center text-xs font-medium text-slate-500 uppercase py-3 px-2">
                          {language === 'tr' ? 'Durum' : 'Status'}
                        </th>
                        <th className="text-right text-xs font-medium text-slate-500 uppercase py-3 px-2">
                          {language === 'tr' ? 'İşlem' : 'Actions'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {skus.map((sku) => (
                        <tr key={sku.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{sku.marketplace_icon}</span>
                              <span className="text-sm font-medium text-slate-700">
                                {sku.marketplace_short_name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-sm text-slate-600">{sku.country_code}</span>
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-sm font-mono text-slate-800">{sku.sku}</span>
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-sm font-mono text-slate-600">
                              {sku.asin || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <span className="text-sm text-slate-700">
                              {sku.listing_price ? `$${sku.listing_price.toFixed(2)}` : '-'}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                              sku.status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : sku.status === 'out_of_stock'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {sku.status === 'active' && <Check className="w-3 h-3 mr-1" />}
                              {sku.status || 'active'}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleEdit(sku)}
                                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                aria-label={language === 'tr' ? 'SKU düzenle' : 'Edit SKU'}
                              >
                                <Edit2 className="w-4 h-4" aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => handleDelete(sku.id)}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                aria-label={language === 'tr' ? 'SKU sil' : 'Delete SKU'}
                              >
                                <Trash2 className="w-4 h-4" aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-slate-50">
          <div className="text-sm text-slate-500">
            {skus.length} SKU
          </div>
          {!isAdding && !editingId && skus.length > 0 && (
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              {language === 'tr' ? 'SKU Ekle' : 'Add SKU'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SKUManagementModal;
